import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { api } from '@/api';
import { toast } from 'sonner';
import {
  ArrowLeft, Clock, AlertCircle, ArrowRight, Loader2,
  ClipboardList, FileText, CheckSquare, Package, FileCheck,
  AlertTriangle, Bell, Scale
} from 'lucide-react';

export default function AwaitingItems() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  
  // Awaiting items by module
  const [awaitingSI, setAwaitingSI] = useState([]);
  const [awaitingRFI, setAwaitingRFI] = useState([]);
  const [awaitingTasks, setAwaitingTasks] = useState([]);
  const [awaitingShopDrawings, setAwaitingShopDrawings] = useState([]);
  const [overdueItems, setOverdueItems] = useState([]);
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    loadAwaitingItems();
  }, []);

  const loadAwaitingItems = async () => {
    setLoading(true);
    try {
      // Load projects first
      const projectsRes = await api.projects.getAll();
      const projectsList = projectsRes.data || [];
      setProjects(projectsList);
      
      // Load SI summary for awaiting items
      const siRes = await api.siDashboard.getSummary().catch(() => ({ data: null }));
      if (siRes.data?.awaiting_items) {
        setAwaitingSI(siRes.data.awaiting_items);
      } else if (siRes.data?.recent_items) {
        // Filter for awaiting status
        setAwaitingSI(siRes.data.recent_items.filter(i => 
          i.status === 'Awaiting Clarification' || i.status === 'Open'
        ));
      }
      
      // Load RFI summary for awaiting items
      const rfiRes = await api.rfiDashboard.getSummary().catch(() => ({ data: null }));
      if (rfiRes.data?.awaiting_items) {
        setAwaitingRFI(rfiRes.data.awaiting_items);
      } else if (rfiRes.data?.recent_items) {
        // Filter for awaiting status
        setAwaitingRFI(rfiRes.data.recent_items.filter(i => 
          i.status === 'Awaiting Response' || i.status === 'Issued'
        ));
      }
      
      // Load tasks that are due soon or overdue
      const tasksRes = await api.get('/tasks').catch(() => ({ data: [] }));
      const tasks = tasksRes.data || [];
      setAwaitingTasks(tasks.filter(t => 
        t.status !== 'Completed' && t.status !== 'Cancelled'
      ).slice(0, 20));
      
      // Load shop drawings pending review (across projects)
      const shopDrawingsAwaiting = [];
      for (const project of projectsList.slice(0, 5)) {
        try {
          const sdRes = await api.get(`/projects/${project.id}/shop-drawings`);
          const pending = (sdRes.data || []).filter(sd => 
            sd.status === 'Under Consultant Review' || sd.status === 'Submitted to Consultant'
          );
          pending.forEach(sd => {
            shopDrawingsAwaiting.push({ ...sd, project_name: project.name, project_id: project.id });
          });
        } catch (e) {
          // Skip if error
        }
      }
      setAwaitingShopDrawings(shopDrawingsAwaiting);
      
      // Collect all overdue items
      const overdue = [];
      
      // Overdue tasks
      tasks.filter(t => t.status !== 'Completed' && t.is_overdue).forEach(t => {
        overdue.push({ ...t, type: 'task', module: 'Tasks' });
      });
      
      // Overdue from SI/RFI summaries
      if (siRes.data?.overdue_items) {
        siRes.data.overdue_items.forEach(item => {
          overdue.push({ ...item, type: 'si', module: 'Site Instructions' });
        });
      }
      if (rfiRes.data?.overdue_items) {
        rfiRes.data.overdue_items.forEach(item => {
          overdue.push({ ...item, type: 'rfi', module: 'RFIs' });
        });
      }
      
      setOverdueItems(overdue);
      
    } catch (error) {
      console.error('Error loading awaiting items:', error);
      toast.error('Failed to load awaiting items');
    } finally {
      setLoading(false);
    }
  };

  const getProjectName = (projectId) => {
    const project = projects.find(p => p.id === projectId);
    return project?.name || 'Unknown Project';
  };

  const renderItemCard = (item, type, onClick) => {
    const getTypeConfig = () => {
      switch (type) {
        case 'si':
          return { icon: ClipboardList, color: 'bg-blue-100 text-blue-700', label: 'SI' };
        case 'rfi':
          return { icon: FileText, color: 'bg-purple-100 text-purple-700', label: 'RFI' };
        case 'task':
          return { icon: CheckSquare, color: 'bg-green-100 text-green-700', label: 'Task' };
        case 'shopDrawing':
          return { icon: Package, color: 'bg-indigo-100 text-indigo-700', label: 'Shop Drawing' };
        case 'event':
          return { icon: AlertTriangle, color: 'bg-amber-100 text-amber-700', label: 'Delay Event' };
        case 'notice':
          return { icon: Bell, color: 'bg-orange-100 text-orange-700', label: 'Notice' };
        case 'claim':
          return { icon: Scale, color: 'bg-red-100 text-red-700', label: 'Claim' };
        default:
          return { icon: FileCheck, color: 'bg-slate-100 text-slate-700', label: 'Item' };
      }
    };

    const config = getTypeConfig();
    const Icon = config.icon;
    const isOverdue = item.is_overdue || item.overdue || (item.age_days && item.age_days > 14);

    return (
      <div
        key={item.id}
        onClick={onClick}
        className={`flex items-center justify-between p-4 border rounded-lg hover:bg-slate-50 cursor-pointer transition-colors ${
          isOverdue ? 'border-red-200 bg-red-50/50' : ''
        }`}
        data-testid={`awaiting-item-${item.id}`}
      >
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <Badge className={config.color}>{config.label}</Badge>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="font-mono font-medium text-sm">
                {item.si_number || item.rfi_number || item.reference_number || item.title?.substring(0, 20) || 'N/A'}
              </span>
              {isOverdue && (
                <Badge className="bg-red-100 text-red-700 text-xs">
                  <AlertCircle className="h-3 w-3 mr-1" />
                  Overdue
                </Badge>
              )}
            </div>
            <p className="text-slate-600 text-sm truncate">
              {item.si_title || item.title || item.description || 'No description'}
            </p>
            <p className="text-slate-400 text-xs mt-1">
              {item.project_name || getProjectName(item.project_id)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <Badge className={`text-xs ${
            item.status === 'Critical' || item.priority === 'Critical' ? 'bg-red-100 text-red-700' :
            item.status === 'Awaiting Clarification' || item.status === 'Awaiting Response' ? 'bg-amber-100 text-amber-700' :
            'bg-slate-100 text-slate-600'
          }`}>
            {item.status || item.priority || 'Pending'}
          </Badge>
          {item.age_days !== undefined && (
            <span className="text-xs text-slate-500">{item.age_days}d</span>
          )}
          <ArrowRight className="h-4 w-4 text-slate-400" />
        </div>
      </div>
    );
  };

  const totalAwaiting = awaitingSI.length + awaitingRFI.length + awaitingTasks.length + awaitingShopDrawings.length;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
          <span className="ml-2 text-slate-500">Loading awaiting items...</span>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-btn">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-barlow font-bold uppercase tracking-tight">Awaiting Items</h1>
              <p className="text-sm text-slate-500">
                {totalAwaiting} items awaiting action • {overdueItems.length} overdue
              </p>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setActiveTab('si')}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <ClipboardList className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-blue-600">{awaitingSI.length}</p>
                  <p className="text-xs text-slate-500 uppercase">Site Instructions</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setActiveTab('rfi')}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <FileText className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-purple-600">{awaitingRFI.length}</p>
                  <p className="text-xs text-slate-500 uppercase">RFIs</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setActiveTab('tasks')}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CheckSquare className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-600">{awaitingTasks.length}</p>
                  <p className="text-xs text-slate-500 uppercase">Tasks</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setActiveTab('overdue')}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <AlertCircle className="h-5 w-5 text-red-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-red-600">{overdueItems.length}</p>
                  <p className="text-xs text-slate-500 uppercase">Overdue</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs for different modules */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-slate-100 mb-4">
            <TabsTrigger value="all" data-testid="tab-all">All ({totalAwaiting})</TabsTrigger>
            <TabsTrigger value="si" data-testid="tab-si">Site Instructions ({awaitingSI.length})</TabsTrigger>
            <TabsTrigger value="rfi" data-testid="tab-rfi">RFIs ({awaitingRFI.length})</TabsTrigger>
            <TabsTrigger value="tasks" data-testid="tab-tasks">Tasks ({awaitingTasks.length})</TabsTrigger>
            <TabsTrigger value="shopDrawings" data-testid="tab-shop-drawings">Shop Drawings ({awaitingShopDrawings.length})</TabsTrigger>
            <TabsTrigger value="overdue" data-testid="tab-overdue">
              <AlertCircle className="h-4 w-4 mr-1 text-red-500" />
              Overdue ({overdueItems.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="all">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  All Awaiting Items
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {awaitingSI.map(item => renderItemCard(item, 'si', () => 
                  navigate(`/projects/${item.project_id}/si/${item.id}`)
                ))}
                {awaitingRFI.map(item => renderItemCard(item, 'rfi', () => 
                  navigate(`/projects/${item.project_id}/rfi/${item.id}`)
                ))}
                {awaitingTasks.map(item => renderItemCard(item, 'task', () => 
                  navigate(`/tasks?id=${item.id}`)
                ))}
                {awaitingShopDrawings.map(item => renderItemCard(item, 'shopDrawing', () => 
                  navigate(`/shop-drawings/${item.id}`)
                ))}
                {totalAwaiting === 0 && (
                  <div className="text-center py-8 text-slate-500">
                    <Clock className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                    <p>No items awaiting action</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="si">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ClipboardList className="h-5 w-5 text-blue-600" />
                  Site Instructions Awaiting Action
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {awaitingSI.map(item => renderItemCard(item, 'si', () => 
                  navigate(`/projects/${item.project_id}/si/${item.id}`)
                ))}
                {awaitingSI.length === 0 && (
                  <div className="text-center py-8 text-slate-500">
                    <ClipboardList className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                    <p>No site instructions awaiting action</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="rfi">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-purple-600" />
                  RFIs Awaiting Response
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {awaitingRFI.map(item => renderItemCard(item, 'rfi', () => 
                  navigate(`/projects/${item.project_id}/rfi/${item.id}`)
                ))}
                {awaitingRFI.length === 0 && (
                  <div className="text-center py-8 text-slate-500">
                    <FileText className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                    <p>No RFIs awaiting response</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="tasks">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckSquare className="h-5 w-5 text-green-600" />
                  Tasks Pending Action
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {awaitingTasks.map(item => renderItemCard(item, 'task', () => 
                  navigate(`/tasks?id=${item.id}`)
                ))}
                {awaitingTasks.length === 0 && (
                  <div className="text-center py-8 text-slate-500">
                    <CheckSquare className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                    <p>No tasks pending action</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="shopDrawings">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Package className="h-5 w-5 text-indigo-600" />
                  Shop Drawings Under Review
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {awaitingShopDrawings.map(item => renderItemCard(item, 'shopDrawing', () => 
                  navigate(`/shop-drawings/${item.id}`)
                ))}
                {awaitingShopDrawings.length === 0 && (
                  <div className="text-center py-8 text-slate-500">
                    <Package className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                    <p>No shop drawings under review</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="overdue">
            <Card className="border-red-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-red-700">
                  <AlertCircle className="h-5 w-5" />
                  Overdue Items - Require Immediate Attention
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {overdueItems.map(item => renderItemCard(item, item.type, () => {
                  if (item.type === 'si') navigate(`/projects/${item.project_id}/si/${item.id}`);
                  else if (item.type === 'rfi') navigate(`/projects/${item.project_id}/rfi/${item.id}`);
                  else if (item.type === 'task') navigate(`/tasks?id=${item.id}`);
                  else navigate('/dashboard');
                }))}
                {overdueItems.length === 0 && (
                  <div className="text-center py-8 text-slate-500">
                    <AlertCircle className="h-12 w-12 mx-auto mb-3 text-green-300" />
                    <p className="text-green-600">No overdue items - great job!</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
